import { useRef, useState, useEffect } from 'react';
import lottie from 'lottie-web';

interface LoadingBusLottieProps {
  size?: 'sm' | 'md' | 'lg';
  label?: string;
}

const sizeMap = {
  sm: { width: 80, height: 80, dots: 'gap-1.5', dot: 'w-1.5 h-1.5', text: 'text-xs', container: 'gap-1' },
  md: { width: 150, height: 150, dots: 'gap-2', dot: 'w-2 h-2', text: 'text-sm', container: 'gap-1' },
  lg: { width: 240, height: 240, dots: 'gap-2.5', dot: 'w-2.5 h-2.5', text: 'text-base', container: 'gap-1' },
};

/**
 * Premium Lottie bus animation — uses lottie-web directly (peer dep of lottie-react).
 * Renders the same bus-transport.json from the Star Line mobile app.
 * JSON is fetched at runtime from /public to keep the JS bundle lean.
 */
export function LoadingBusLottie({ size = 'md', label }: LoadingBusLottieProps) {
  const s = sizeMap[size];
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const anim = lottie.loadAnimation({
      container: containerRef.current,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: '/bus-transport.json',
    });
    anim.addEventListener('DOMLoaded', () => setLoaded(true));
    return () => anim.destroy();
  }, []);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label || 'Loading'}
      className={`flex flex-col items-center ${s.container}`}
    >
      <div
        ref={containerRef}
        style={{ width: s.width, height: s.height, opacity: loaded ? 1 : 0, transition: 'opacity 0.3s' }}
      />

      {/* Animated road dots */}
      <div className={`flex items-center ${s.dots}`}>
        {[0, 1, 2, 3, 4].map(i => (
          <div
            key={i}
            className={`${s.dot} rounded-full bg-primary/40 loading-lottie-dot`}
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </div>

      {/* Label */}
      {label && (
        <p className={`${s.text} font-medium text-muted-foreground loading-lottie-text`}>
          {label}
        </p>
      )}

      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          .loading-lottie-dot {
            animation: lottieDot 1.2s ease-in-out infinite;
          }
          .loading-lottie-text {
            animation: lottieTextPulse 2s ease-in-out infinite;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .loading-lottie-dot,
          .loading-lottie-text {
            animation: none !important;
          }
          .loading-lottie-dot { opacity: 0.5; }
        }
        @keyframes lottieDot {
          0%, 100% { opacity: 0.25; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
        @keyframes lottieTextPulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default LoadingBusLottie;
